import { useMemo } from 'react';

import type {
  AllDimensions,
  AllMeasures,
  CubeMemberTypes,
  TypedCubeQuery
} from 'shared/src/generated/cube-types';
import type { CubeMemberInfo } from 'shared/src/trpc-types/cube-load-trpc-types';
import type { AllCubeFields } from 'shared/src/domain/metrics/cube-field-registry';
import { trpc } from '@/utils/trpc-client';
import { CubeQuerySchema } from 'shared/src/api/cube-query-schema';
import { formatRows } from '@/features/line-items/cube-format-utils';

export type QueryResult<
  D extends readonly AllDimensions[],
  M extends readonly AllMeasures[]
> = Array<{ [K in D[number]]: CubeMemberTypes[K] } & { [K in M[number]]: CubeMemberTypes[K] }>;

export type FormattedResult<
  D extends readonly AllDimensions[],
  M extends readonly AllMeasures[]
> = Array<{ [K in D[number]]: string } & { [K in M[number]]: string }>;

export type MemberMetaFor<D extends readonly AllDimensions[], M extends readonly AllMeasures[]> = {
  [K in D[number] | M[number]]: CubeMemberInfo &
    (K extends keyof AllCubeFields ? AllCubeFields[K] : {});
};

type CubeRenderData<D extends readonly AllDimensions[], M extends readonly AllMeasures[]> = {
  status: 'success';
  rows: QueryResult<D, M>;
  formattedRows: FormattedResult<D, M>;
  totalRows: number;
  memberMeta: MemberMetaFor<D, M>;
};

type CubeLoadError = {
  status: 'error';
  error: string;
};

type CubeLoading = {
  status: 'loading';
};

export type CubeQueryResult<
  D extends readonly AllDimensions[] = readonly [],
  M extends readonly AllMeasures[] = readonly []
> = CubeRenderData<D, M> | CubeLoadError | CubeLoading;

export function useCubeQuery<
  const D extends readonly AllDimensions[] = readonly [],
  const M extends readonly AllMeasures[] = readonly []
>(query: TypedCubeQuery<D, M>): CubeQueryResult<D, M> {
  const parsedQuery = CubeQuerySchema.parse(query);
  const { data, isLoading, error } = trpc.cubeLoad.useQuery(parsedQuery);

  const formattedRows = useMemo(() => {
    if (!data) return undefined;
    return formatRows(data.rows, data.memberMeta);
  }, [data]);

  if (isLoading || !data) {
    return { status: 'loading' };
  } else if (error) {
    return { status: 'error', error: error.message };
  } else {
    return {
      status: 'success',
      rows: data.rows as QueryResult<D, M>,
      formattedRows: formattedRows as FormattedResult<D, M>,
      totalRows: data.totalRows,
      memberMeta: data.memberMeta as MemberMetaFor<D, M>
    };
  }
}
